import { buildAllLensPerLiftCardsForWeek } from "./buildInsightsPerLiftCards";
import {
  buildLensWeekSummaryForPanel,
  computeLensLiftCoverageFromScan,
  INSIGHTS_PER_LIFT_LENS_IDS,
} from "./insightsLensCompVolume";
import {
  buildInsightsVolumeCoverageAlerts,
  buildInsightsVolumeCoverageMetrics,
} from "./insightsVolumeCoverage";
import { computeLiftBalanceForLens, lensSupportsLiftBalance } from "./insightsLiftBalance";
import {
  buildLensCompFooterPayload,
  buildOverviewMetricsFromDisplayScalars,
  collectResolvedExercisesForWeek,
  weekDisplayScalarsFromScan,
} from "./insightsWeekOverview";
import { muscleRowsWithWeekOverWeekDeltas } from "./insightsDistributions";
import { scanWeekExercises } from "./insightsWeekScan";
import {
  exerciseHasCompletedOutcomes,
  INSIGHTS_DATA_SOURCE,
} from "./insightsSetRowMath";

/**
 * Count sessions for workoutsPerWeek. In OUTCOMES mode, only sessions where at
 * least one exercise has a completed logged outcome count as a training day.
 * @param {object[]|undefined} sessions
 * @param {string} dataSource
 * @returns {number}
 */
function countSessions(sessions, dataSource) {
  if (!Array.isArray(sessions)) return 0;
  if (dataSource !== INSIGHTS_DATA_SOURCE.OUTCOMES) return sessions.length;
  return sessions.filter((s) =>
    s.resolvedExercises.some(exerciseHasCompletedOutcomes)
  ).length;
}

/** Map week series row to panel week.
 * @param {ReturnType<typeof buildProgramInsightsWeekSeries>[number]} weekRow
 */
function mapWeekSeriesRowToPanelWeek(weekRow) {
  const byLens = weekRow.perLiftCardsByLens ?? {};
  const footers = weekRow.lensCompFooters ?? {};
  return {
    weekIndex: weekRow.weekIndex,
    overview: weekRow.overview,
    muscles: weekRow.muscles,
    repRanges: weekRow.repRanges,
    rpeDistribution: weekRow.rpeDistribution,
    volumeCoverage: weekRow.volumeCoverage,
    perLiftCards: byLens.powerlifting ?? [],
    olympicPerLiftCards: byLens.olympic_weightlifting ?? [],
    strongmanPerLiftCards: byLens.strongman ?? [],
    streetliftingPerLiftCards: byLens.streetlifting ?? [],
    powerliftingWeekSummary: buildLensWeekSummaryForPanel(
      "powerlifting",
      footers.powerlifting
    ),
    olympicWeekSummary: buildLensWeekSummaryForPanel(
      "olympic_weightlifting",
      footers.olympic_weightlifting
    ),
    strongmanWeekSummary: buildLensWeekSummaryForPanel(
      "strongman",
      footers.strongman
    ),
    streetliftingWeekSummary: buildLensWeekSummaryForPanel(
      "streetlifting",
      footers.streetlifting
    ),
  };
}

/** Function to build program insights week series.
 * @param {{ weeks: object[] }} resolution
 * @param {{ fromMetric?: Function, unitsKey?: string, intensityMeasure?: "rpe"|"rir", insightsVolumeSuffix?: string, dataSource?: string }} [options]
 */
export function buildProgramInsightsWeekSeries(
  resolution,
  {
    fromMetric,
    unitsKey = "metric",
    intensityMeasure = "rpe",
    insightsVolumeSuffix = " kg",
    dataSource = INSIGHTS_DATA_SOURCE.PRESCRIBED,
  } = {}
) {
  const weekBlocks = resolution?.weeks ?? [];
  const exerciseLists = weekBlocks.map((w) => collectResolvedExercisesForWeek(w));
  const scans = exerciseLists.map((ex) =>
    scanWeekExercises(ex, { intensityMeasure, dataSource })
  );
  const displayScalars = scans.map((scan) =>
    weekDisplayScalarsFromScan(scan, fromMetric, unitsKey, intensityMeasure)
  );

  const perLiftOptions = {
    fromMetric,
    unitsKey,
    insightsVolumeSuffix,
    intensityMeasure,
    dataSource,
  };
  /** @type {Record<string, Map<string, object>>|null} */
  let prevPerLiftScalarsByLens = null;
  const perLiftCardsByWeek = exerciseLists.map((weekExercises, idx) => {
    const { perLiftCardsByLens, scalarsByLens } = buildAllLensPerLiftCardsForWeek(
      weekExercises,
      prevPerLiftScalarsByLens,
      perLiftOptions,
      scans[idx].perLiftScalarsByLens
    );
    prevPerLiftScalarsByLens = scalarsByLens;
    return perLiftCardsByLens;
  });

  return weekBlocks.map((rw, idx) => {
    const scan = scans[idx];
    const prev =
      idx > 0
        ? {
            sessionCount: countSessions(weekBlocks[idx - 1].sessions, dataSource),
            muscles: scans[idx - 1].muscles,
            amrapSets: scans[idx - 1].amrapSets,
          }
        : null;
    const sessionCount = countSessions(rw.sessions, dataSource);

    const lensCompFooters = Object.fromEntries(
      INSIGHTS_PER_LIFT_LENS_IDS.map((lensId) => {
        const liftBalance = lensSupportsLiftBalance(lensId)
          ? computeLiftBalanceForLens(
              lensId,
              scan.perLiftScalarsByLens[lensId]
            )
          : null;
        const coverage = computeLensLiftCoverageFromScan(scan, lensId);
        return [
          lensId,
          buildLensCompFooterPayload(
            scan.lensCompVolumeKg[lensId] ?? 0,
            fromMetric,
            unitsKey,
            insightsVolumeSuffix,
            {
              ...coverage,
              liftBalance,
            }
          ),
        ];
      })
    );

    return {
      weekIndex: rw.weekIndex,
      overview: buildOverviewMetricsFromDisplayScalars(
        displayScalars[idx],
        idx > 0 ? displayScalars[idx - 1] : null
      ),
      muscles: muscleRowsWithWeekOverWeekDeltas(
        scan.muscles,
        idx > 0 ? scans[idx - 1].muscles : null
      ),
      repRanges: scan.repRanges,
      rpeDistribution: scan.rpeDistribution,
      volumeCoverage: {
        metrics: buildInsightsVolumeCoverageMetrics(
          scan.muscles,
          scan.amrapSets,
          sessionCount,
          prev
        ),
        alerts: buildInsightsVolumeCoverageAlerts(scan.muscles),
      },
      lensCompFooters,
      perLiftCardsByLens: perLiftCardsByWeek[idx] ?? {},
      powerliftingSbd: lensCompFooters.powerlifting,
    };
  });
}

/** Function to build program insights distribution series.
 * @param {{ weeks: object[] }} resolution
 * @param {Parameters<typeof buildProgramInsightsWeekSeries>[1]} [options]
 */
export function buildProgramInsightsPanelWeeks(resolution, options = {}) {
  return buildProgramInsightsWeekSeries(resolution, options).map(
    mapWeekSeriesRowToPanelWeek
  );
}

/** Function to build program insights overview series.
 * @param {{ weeks: object[] }} resolution
 * @param {Function} [fromMetric]
 * @param {string} unitsKey
 */
export function buildProgramInsightsOverviewSeries(
  resolution,
  fromMetric,
  unitsKey
) {
  return buildProgramInsightsWeekSeries(resolution, { fromMetric, unitsKey }).map(
    (w) => w.overview
  );
}

/** Function to build program insights distribution series.
 * @param {{ weeks: object[] }} resolution
 * @param {{ intensityMeasure?: "rpe"|"rir" }} [options]
 */
export function buildProgramInsightsDistributionSeries(
  resolution,
  { intensityMeasure = "rpe" } = {}
) {
  return buildProgramInsightsWeekSeries(resolution, { intensityMeasure }).map(
    (w) => ({
      muscles: w.muscles,
      repRanges: w.repRanges,
      rpeDistribution: w.rpeDistribution,
    })
  );
}
